"use client";

import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  CheckmarkCircle01Icon,
  LockIcon,
  Message01Icon,
  Shield01Icon,
  Tick01Icon,
} from "@hugeicons/core-free-icons";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface OnboardingFlowProps {
  onComplete: () => void;
}

const steps = [
  {
    id: "welcome",
    title: "Welcome to Onera",
    subtitle: "Private AI chat you can actually trust",
  },
  {
    id: "recovery",
    title: "Your Recovery Phrase",
    subtitle: "The key to your encrypted data",
  },
];

export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = steps[currentStep];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-white dark:bg-gray-900">
      <div className="relative w-full max-w-lg rounded-3xl bg-white dark:bg-gray-850 border border-gray-100 dark:border-gray-850 p-6 sm:p-8">
        {/* Progress indicator */}
        <div className="mb-8 flex justify-center gap-2">
          {steps.map((_, index) => (
            <div
              key={index}
              className={cn(
                "h-1.5 rounded-full transition-all duration-300",
                index === currentStep
                  ? "w-8 bg-primary"
                  : index < currentStep
                    ? "w-4 bg-primary/50"
                    : "w-4 bg-gray-200 dark:bg-gray-800",
              )}
            />
          ))}
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {step.id === "welcome" && <WelcomeStep />}
            {step.id === "recovery" && <RecoveryStep />}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="mt-8 flex gap-3">
          {currentStep > 0 && (
            <Button variant="outline" onClick={handleBack} className="gap-2">
              <HugeiconsIcon icon={ArrowLeft01Icon} className="h-4 w-4" />
              Back
            </Button>
          )}
          <Button onClick={handleNext} className="flex-1 gap-2">
            {currentStep === steps.length - 1 ? (
              <>
                I Understand
                <HugeiconsIcon icon={Tick01Icon} className="h-4 w-4" />
              </>
            ) : (
              <>
                Continue
                <HugeiconsIcon icon={ArrowRight01Icon} className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>

        {/* Skip link - only on first step */}
        {currentStep === 0 && (
          <button
            onClick={onComplete}
            className="mt-4 w-full text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            Skip introduction
          </button>
        )}
      </div>
    </div>
  );
}

function WelcomeStep() {
  return (
    <div className="text-center">
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gray-50 dark:bg-gray-850/50 border border-gray-100 dark:border-gray-850">
        <HugeiconsIcon
          icon={Message01Icon}
          className="h-9 w-9 text-gray-900/80 dark:text-gray-100/80"
        />
      </div>
      <h1 className="text-3xl font-bold tracking-tight mb-3">
        Welcome to Onera
      </h1>
      <p className="text-lg text-gray-500 dark:text-gray-400 mb-8">
        Private AI chat that actually keeps your conversations private.
      </p>
      <div className="space-y-3 text-left">
        <Feature icon={LockIcon} text="Your chats are end-to-end encrypted" />
        <Feature
          icon={Shield01Icon}
          text="Processed in secure hardware we can't access"
        />
        <Feature
          icon={CheckmarkCircle01Icon}
          text="Your browser verifies every connection"
        />
      </div>
    </div>
  );
}

function RecoveryStep() {
  return (
    <div className="text-center">
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500/15 via-orange-500/15 to-red-500/10 ring-1 ring-amber-500/20">
        <HugeiconsIcon
          icon={LockIcon}
          className="h-10 w-10 text-amber-600 dark:text-amber-400"
        />
      </div>
      <h1 className="text-3xl font-bold tracking-tight mb-3">
        Your Recovery Phrase
      </h1>
      <p className="text-gray-500 dark:text-gray-400 mb-6">
        In the next step, you'll receive a recovery phrase. This is important.
      </p>

      <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-amber-500/8 to-orange-500/5 p-4 text-left space-y-3">
        <div className="flex gap-3">
          <HugeiconsIcon
            icon={Shield01Icon}
            className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
          />
          <div>
            <p className="font-medium text-sm text-amber-900 dark:text-amber-100">
              It's your backup key
            </p>
            <p className="text-xs text-amber-800/80 dark:text-amber-200/70">
              Use it to recover your data if you lose access to your passkey or
              password.
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <HugeiconsIcon
            icon={Shield01Icon}
            className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
          />
          <div>
            <p className="font-medium text-sm text-amber-900 dark:text-amber-100">
              We can't reset it
            </p>
            <p className="text-xs text-amber-800/80 dark:text-amber-200/70">
              This is by design. Only you have access to your data.
            </p>
          </div>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
        Save it somewhere safe. You'll see it after setting up your unlock
        method.
      </p>
    </div>
  );
}

function Feature({ icon, text }: { icon: IconSvgElement; text: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-gray-50 dark:bg-gray-850/50 border border-gray-100 dark:border-gray-850 p-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/12">
        <HugeiconsIcon icon={icon} size={16} className="text-primary" />
      </div>
      <span className="text-sm font-medium">{text}</span>
    </div>
  );
}
